import { AppState } from './state.js';
import { getStadiumName, getStadiumMetadata, STADIUM_METADATA } from './stadiumHelper.js';
import { Offcanvas } from 'bootstrap';

export class UIController {
  constructor(stadiumLoader, sharedScene) {
    this.loader = stadiumLoader;
    this.sharedScene = sharedScene;

    if (import.meta.env.PROD) {
      // Remove refinement panel (adjustments, model uploads, import/export) from DOM
      const refinementPanel = document.getElementById('refinement-panel');
      if (refinementPanel) refinementPanel.remove();

      // Remove maps toggle container from DOM
      const mapsToggle = document.getElementById('maps-toggle');
      if (mapsToggle) {
        const switchContainer = mapsToggle.closest('.control-group');
        if (switchContainer) switchContainer.remove();
        else mapsToggle.remove();
      }

      // Remove maps container from DOM
      const mapContainer = document.getElementById('map-container');
      if (mapContainer) mapContainer.remove();

      AppState.updateState({ mapsEnabled: false });
    }

    // UI Input Elements
    this.mapsToggle = document.getElementById('maps-toggle');
    this.datePicker = document.getElementById('date-picker');
    this.timeSlider = document.getElementById('time-slider');
    this.timeDisplay = document.getElementById('time-display');
    this.latInput = document.getElementById('lat-input');
    this.lonInput = document.getElementById('lon-input');
    this.altSlider = document.getElementById('alt-slider');
    this.rotationSlider = document.getElementById('rotation-slider');
    this.scaleSlider = document.getElementById('scale-slider');
    this.stadiumSelect = document.getElementById('stadium-select');

    // UI Action Elements
    this.btnExport = document.getElementById('btn-export');
    this.btnImport = document.getElementById('btn-import');
    this.fileImport = document.getElementById('file-import');
    this.btnReset = document.getElementById('btn-reset');
    this.btnNextGame = document.getElementById('btn-next-game');
    this.gameSelect = document.getElementById('game-schedule-select');

    // Labels & Overlays
    this.stadiumNameSelect = document.getElementById('stadium-name-select');
    this.loadingOverlay = document.getElementById('loading-overlay');
    this.uiPanel = document.getElementById('ui-panel');
    this.drawerTrigger = document.getElementById('drawer-trigger');

    // Value Display Badges
    this.altVal = document.getElementById('alt-val');
    this.rotationVal = document.getElementById('rotation-val');
    this.scaleVal = document.getElementById('scale-val');

    // Initialize Bootstrap Offcanvas
    this.bsOffcanvas = new Offcanvas(this.uiPanel);
    this.lastFetchedTeamId = null;

    this._populateStadiumSelector();
    this._bindEvents();
    this._setupSubscription();
    this._setupSecretKeys();
  }

  _bindEvents() {
    // Stadium select dropdown handling
    if (this.stadiumSelect) {
      this.stadiumSelect.addEventListener('change', (event) => {
        const fileName = event.target.value;
        if (fileName) {
          this._loadStadiumByName(fileName);
        }
      });
    }

    if (this.stadiumNameSelect) {
      this.stadiumNameSelect.addEventListener('change', (event) => {
        const fileName = event.target.value;
        if (fileName) {
          this._loadStadiumByName(fileName);
        }
      });
    }

    // Toggle Maps Layer
    if (this.mapsToggle) {
      this.mapsToggle.addEventListener('change', (event) => {
        AppState.updateState({ mapsEnabled: event.target.checked });
      });
    }

    // Date simulation
    if (this.datePicker) {
      this.datePicker.addEventListener('change', (event) => {
        AppState.updateState({ date: event.target.value });
      });
    }

    // Time simulation
    if (this.timeSlider) {
      this.timeSlider.addEventListener('input', (event) => {
        AppState.updateState({ time: parseFloat(event.target.value) });
      });
    }

    // Coordinates inputs
    if (this.latInput) {
      this.latInput.addEventListener('change', (event) => {
        const val = parseFloat(event.target.value);
        if (!isNaN(val)) AppState.updateState({ lat: val });
      });
    }
    if (this.lonInput) {
      this.lonInput.addEventListener('change', (event) => {
        const val = parseFloat(event.target.value);
        if (!isNaN(val)) AppState.updateState({ lng: val });
      });
    }

    // Adjustment sliders
    if (this.altSlider) {
      this.altSlider.addEventListener('input', (event) => {
        AppState.updateState({ alt: parseFloat(event.target.value) });
      });
    }
    if (this.rotationSlider) {
      this.rotationSlider.addEventListener('input', (event) => {
        AppState.updateState({ rotation: parseFloat(event.target.value) });
      });
    }
    if (this.scaleSlider) {
      this.scaleSlider.addEventListener('input', (event) => {
        AppState.updateState({ scale: parseFloat(event.target.value) });
      });
    }

    // Coordinate fine-nudging events
    const nudgeUp = document.getElementById('nudge-up');
    if (nudgeUp) nudgeUp.addEventListener('click', () => this._nudgeCoordinates(0.000001, 0));
    const nudgeDown = document.getElementById('nudge-down');
    if (nudgeDown) nudgeDown.addEventListener('click', () => this._nudgeCoordinates(-0.000001, 0));
    const nudgeLeft = document.getElementById('nudge-left');
    if (nudgeLeft) nudgeLeft.addEventListener('click', () => this._nudgeCoordinates(0, -0.000001));
    const nudgeRight = document.getElementById('nudge-right');
    if (nudgeRight) nudgeRight.addEventListener('click', () => this._nudgeCoordinates(0, 0.000001));

    // Reset settings
    if (this.btnReset) {
      this.btnReset.addEventListener('click', () => {
        if (confirm('Are you sure you want to reset all alignment settings?')) {
          AppState.resetToDefaults();
        }
      });
    }

    // Export alignment coordinates
    if (this.btnExport) {
      this.btnExport.addEventListener('click', () => {
        this._exportAlignment();
      });
    }

    // Trigger file import dialog
    if (this.btnImport) {
      this.btnImport.addEventListener('click', () => {
        this.fileImport.click();
      });
    }

    // Read and import alignment files
    if (this.fileImport) {
      this.fileImport.addEventListener('change', (event) => {
        this._importAlignment(event);
      });
    }

    // Sync next available game start time
    if (this.btnNextGame) {
      this.btnNextGame.addEventListener('click', () => this._syncNextGame());
    }

    // Sync dropdown selected game
    if (this.gameSelect) {
      this.gameSelect.addEventListener('change', (event) => this._onGameSelectChange(event));
    }

    // Floating Menu Button Trigger Offcanvas
    if (this.drawerTrigger) {
      this.drawerTrigger.addEventListener('click', () => {
        this.bsOffcanvas.show();
      });
    }

    // Toggle floating trigger button opacity on Offcanvas transitions
    if (this.uiPanel) {
      this.uiPanel.addEventListener('show.bs.offcanvas', () => {
        if (this.drawerTrigger) this.drawerTrigger.classList.add('drawer-open');
      });
      this.uiPanel.addEventListener('hidden.bs.offcanvas', () => {
        if (this.drawerTrigger) this.drawerTrigger.classList.remove('drawer-open');
      });
    }

    // Header close drawer bind
    if (this.uiPanel) {
      const header = this.uiPanel.querySelector('h2');
      if (header) {
        header.style.cursor = 'pointer';
        header.addEventListener('click', () => {
          this.bsOffcanvas.hide();
        });
      }
    }
  }

  /**
   * Adjusts the current latitude and longitude by a micro-increment and updates state.
   */
  _nudgeCoordinates(dLat, dLng) {
    const state = AppState.getState();
    const newLat = parseFloat((state.lat + dLat).toFixed(6));
    const newLng = parseFloat((state.lng + dLng).toFixed(6));
    AppState.updateState({ lat: newLat, lng: newLng });
  }

  _setupSubscription() {
    // Bidirectional Binding: Sync State changes back into UI inputs
    AppState.subscribe((state) => {
      if (this.mapsToggle && this.mapsToggle.checked !== state.mapsEnabled) {
        this.mapsToggle.checked = state.mapsEnabled;
      }
      if (this.datePicker && this.datePicker.value !== state.date) {
        this.datePicker.value = state.date;
      }
      if (this.timeSlider && parseFloat(this.timeSlider.value) !== state.time) {
        this.timeSlider.value = state.time;
      }
      if (this.latInput && parseFloat(this.latInput.value) !== state.lat) {
        this.latInput.value = state.lat;
      }
      if (this.lonInput && parseFloat(this.lonInput.value) !== state.lng) {
        this.lonInput.value = state.lng;
      }
      if (this.altSlider && parseFloat(this.altSlider.value) !== state.alt) {
        this.altSlider.value = state.alt;
      }
      if (this.rotationSlider && parseFloat(this.rotationSlider.value) !== state.rotation) {
        this.rotationSlider.value = state.rotation;
      }
      if (this.scaleSlider && parseFloat(this.scaleSlider.value) !== state.scale) {
        this.scaleSlider.value = state.scale;
      }
      if (this.stadiumSelect && this.stadiumSelect.value !== state.stadiumName) {
        this.stadiumSelect.value = state.stadiumName;
      }
      if (this.stadiumNameSelect && this.stadiumNameSelect.value !== state.stadiumName) {
        this.stadiumNameSelect.value = state.stadiumName;
      }

      // Update value display badges
      if (this.altVal) this.altVal.innerText = state.alt.toFixed(1) + 'm';
      if (this.rotationVal) this.rotationVal.innerText = state.rotation.toFixed(1) + '°';
      if (this.scaleVal) this.scaleVal.innerText = state.scale.toFixed(2) + 'x';

      // Format time label (e.g. 15.25 -> 3:15 PM)
      if (this.timeDisplay) {
        const hours = Math.floor(state.time);
        const minutes = Math.floor((state.time % 1) * 60);
        const ampm = hours >= 12 ? 'PM' : 'AM';
        let displayHour = hours > 12 ? hours - 12 : hours;
        if (displayHour === 0) displayHour = 12;
        this.timeDisplay.innerText = `${displayHour}:${minutes.toString().padStart(2, '0')} ${ampm}`;
      }

      // Fetch upcoming games schedule if team changes
      const meta = getStadiumMetadata(state.stadiumName);
      if (meta) {
        if (this.lastFetchedTeamId !== meta.teamId) {
          this.lastFetchedTeamId = meta.teamId;
          this._populateScheduleDropdown(meta);

          // Automatically sync AppState coordinates if they differ from the stadium's metadata
          if (state.lat !== meta.lat || state.lng !== meta.lng) {
            AppState.updateState({
              lat: meta.lat,
              lng: meta.lng
            });
          }
        }
      } else {
        this.lastFetchedTeamId = null;
        if (this.gameSelect) {
          this.gameSelect.innerHTML = '<option value="">⚾ Sync unavailable for custom models</option>';
          this.gameSelect.disabled = true;
        }
      }
    });
  }

  _exportAlignment() {
    const state = AppState.getState();
    const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(state, null, 2));
    const downloadAnchor = document.createElement('a');
    downloadAnchor.setAttribute("href", dataStr);
    
    // Dynamic naming based on active model filename
    const teamAbbr = state.stadiumName 
      ? state.stadiumName.replace('Stadium_', '').replace('.glb', '')
      : 'alignment';

    downloadAnchor.setAttribute("download", `stadium_${teamAbbr}_alignment.json`);
    document.body.appendChild(downloadAnchor);
    downloadAnchor.click();
    downloadAnchor.remove();
  }

  _importAlignment(event) {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const imported = JSON.parse(e.target.result);
        
        const updates = {};
        if (typeof imported.lat === 'number') updates.lat = imported.lat;
        if (typeof imported.lng === 'number') updates.lng = imported.lng;
        if (typeof imported.alt === 'number') updates.alt = imported.alt;
        if (typeof imported.rotation === 'number') updates.rotation = imported.rotation;
        if (typeof imported.scale === 'number') updates.scale = imported.scale;
        if (typeof imported.date === 'string') updates.date = imported.date;
        if (typeof imported.time === 'number') updates.time = imported.time;
        if (typeof imported.mapsEnabled === 'boolean') updates.mapsEnabled = imported.mapsEnabled;

        AppState.updateState(updates);
        alert('Alignment loaded successfully!');
      } catch (err) {
        alert('Failed to parse alignment file. Ensure it is a valid alignment JSON.');
      }
    };
    reader.readAsText(file);
    
    // Clear input to allow uploading the same file again
    this.fileImport.value = '';
  }

  /**
   * Captures window key events to toggle refinement settings via 'r' keypresses
   */
  _setupSecretKeys() {
    if (import.meta.env.PROD) return;

    let rCount = 0;
    let lastRTime = 0;

    window.addEventListener('keydown', (event) => {
      // Ignore key events when typing in standard inputs
      if (document.activeElement.tagName === 'INPUT' || document.activeElement.tagName === 'TEXTAREA') {
        return;
      }

      if (event.key === 'r' || event.key === 'R') {
        const now = Date.now();
        if (now - lastRTime > 1500) {
          rCount = 1;
        } else {
          rCount++;
        }
        lastRTime = now;

        if (rCount === 3) {
          rCount = 0;
          const panel = document.getElementById('refinement-panel');
          if (panel) {
            const isUnlocked = panel.classList.toggle('unlocked');
            this._showNotification(isUnlocked ? 'Refinement Panel Unlocked 🔓' : 'Refinement Panel Locked 🔒');
          }
        }
      }
    });
  }

  /**
   * Dynamically populates the stadium dropdown from STADIUM_METADATA
   */
  _populateStadiumSelector() {
    const selectors = [this.stadiumSelect, this.stadiumNameSelect].filter(Boolean);
    if (selectors.length === 0) return;

    const options = Object.entries(STADIUM_METADATA).map(([code, meta]) => {
      return {
        value: `Stadium_${code}.glb`,
        text: meta.name
      };
    });

    // Sort options alphabetically by stadium name
    options.sort((a, b) => a.text.localeCompare(b.text));

    selectors.forEach(selectEl => {
      // Clear and populate dropdown
      selectEl.innerHTML = '';
      options.forEach(opt => {
        const optionEl = document.createElement('option');
        optionEl.value = opt.value;
        optionEl.textContent = opt.text;
        selectEl.appendChild(optionEl);
      });
    });
  }

  /**
   * Loads a stadium by its GLB filename, performing LFS checks first
   */
  async _loadStadiumByName(fileName) {
    if (!fileName) return;

    this.loadingOverlay.style.display = 'flex';
    this.loadingOverlay.innerText = 'Loading Stadium Model...';

    let objectUrl = null;
    try {
      const url = `/models/${fileName}`;

      // Fetch the GLB file as a blob to check for Git LFS pointer files
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const blob = await response.blob();
      if (blob.size < 1000) {
        const text = await blob.text();
        if (text.startsWith('version https://git-lfs.github.com/spec/v1')) {
          alert(`⚠️ Cannot load "${fileName}": This is a Git LFS pointer file, not the actual 3D model.\n\nPlease run the download script "get stadium models/stadiums.sh" to download the real GLB files before switching.`);
          // Revert selection
          if (this.stadiumSelect) {
            this.stadiumSelect.value = AppState.getState().stadiumName;
          }
          if (this.stadiumNameSelect) {
            this.stadiumNameSelect.value = AppState.getState().stadiumName;
          }
          return;
        }
      }

      objectUrl = URL.createObjectURL(blob);
      const result = await this.loader.loadFromUrl(objectUrl, this.sharedScene.stadiumGroup);

      AppState.updateState({
        baseScale: result.baseScale,
        stadiumName: fileName
      });

    } catch (err) {
      alert('Failed to load stadium model: ' + err.message);
      console.error(err);
      // Revert selection
      if (this.stadiumSelect) {
        this.stadiumSelect.value = AppState.getState().stadiumName;
      }
      if (this.stadiumNameSelect) {
        this.stadiumNameSelect.value = AppState.getState().stadiumName;
      }
    } finally {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
      this.loadingOverlay.style.display = 'none';
      this.loadingOverlay.innerText = 'Loading...';
    }
  }

  /**
   * Helper to display a premium toast notification on screen
   */
  _showNotification(message) {
    let toast = document.getElementById('ui-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'ui-toast';
      toast.style.position = 'absolute';
      toast.style.bottom = '30px';
      toast.style.left = '50%';
      toast.style.transform = 'translateX(-50%)';
      toast.style.background = 'rgba(13, 17, 28, 0.95)';
      toast.style.border = '1px solid rgba(56, 189, 248, 0.3)';
      toast.style.color = '#38bdf8';
      toast.style.padding = '10px 20px';
      toast.style.borderRadius = '24px';
      toast.style.fontSize = '12px';
      toast.style.fontWeight = '600';
      toast.style.boxShadow = '0 10px 25px rgba(0,0,0,0.5), 0 0 10px rgba(56,189,248,0.2)';
      toast.style.zIndex = '9999';
      toast.style.pointerEvents = 'none';
      toast.style.transition = 'opacity 0.25s ease';
      toast.style.fontFamily = 'system-ui, -apple-system, sans-serif';
      document.body.appendChild(toast);
    }

    toast.innerText = message;
    toast.style.opacity = '1';

    if (this._toastTimeout) clearTimeout(this._toastTimeout);
    this._toastTimeout = setTimeout(() => {
      toast.style.opacity = '0';
    }, 1800);
  }

  /**
   * Syncs the simulation date and time to the next available home game at the loaded stadium
   */
  async _syncNextGame() {
    const state = AppState.getState();
    const meta = getStadiumMetadata(state.stadiumName);
    
    if (!meta) {
      this._showNotification('⚾ Schedule sync only available for MLB stadium models.');
      return;
    }

    const originalText = this.btnNextGame.innerHTML;
    this.btnNextGame.innerText = '⚾ Fetching schedule...';
    this.btnNextGame.disabled = true;

    try {
      // Calculate today and 30 days out in local YYYY-MM-DD format
      const now = new Date();
      const formatDateStr = (d) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const r = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${r}`;
      };

      const startDate = formatDateStr(now);
      const future = new Date();
      future.setDate(now.getDate() + 30);
      const endDate = formatDateStr(future);

      const url = `https://statsapi.mlb.com/api/v1/schedule?sportId=1&teamId=${meta.teamId}&startDate=${startDate}&endDate=${endDate}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error('Network response not ok');
      const data = await response.json();

      let nextHomeGame = null;
      let fallbackGame = null; // first home game found regardless of status

      if (data.dates) {
        for (const dateInfo of data.dates) {
          for (const game of dateInfo.games) {
            // Check if it's a home game
            const isHome = game.teams.home.team.id === meta.teamId;
            if (isHome) {
              if (!fallbackGame) fallbackGame = game;
              const isFinishedOrPostponed = ['F', 'O', 'D', 'C'].includes(game.status.codedGameState);
              if (!isFinishedOrPostponed) {
                nextHomeGame = game;
                break;
              }
            }
          }
          if (nextHomeGame) break;
        }
      }

      // Fallback to finished game if no upcoming games in the window
      const game = nextHomeGame || fallbackGame;

      if (!game) {
        this._showNotification(`⚾ No games scheduled for ${meta.name} in next 30 days.`);
        return;
      }

      // Convert UTC time to venue timezone
      const utcDate = new Date(game.gameDate);
      const partsFormatter = new Intl.DateTimeFormat('en-US', {
        timeZone: meta.tz,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });

      const parts = partsFormatter.formatToParts(utcDate);
      const partVal = (type) => parts.find(p => p.type === type).value;

      const localYear = partVal('year');
      const localMonth = partVal('month');
      const localDay = partVal('day');
      const localHour = parseInt(partVal('hour'));
      const localMinute = parseInt(partVal('minute'));

      const gameLocalDateStr = `${localYear}-${localMonth}-${localDay}`;
      const gameLocalTimeDec = parseFloat((localHour + localMinute / 60).toFixed(2));

      // Update state
      AppState.updateState({
        date: gameLocalDateStr,
        time: gameLocalTimeDec
      });

      // Format printable start time
      const displayHour = localHour > 12 ? localHour - 12 : (localHour === 0 ? 12 : localHour);
      const ampm = localHour >= 12 ? 'PM' : 'AM';
      const timeStr = `${displayHour}:${localMinute.toString().padStart(2, '0')} ${ampm}`;

      const vs = game.teams.away.team.name;
      const note = nextHomeGame ? '' : ' (Completed)';
      this._showNotification(`⚾ Synced next game vs. ${vs} on ${gameLocalDateStr} at ${timeStr}${note}`);

    } catch (err) {
      console.error(err);
      this._showNotification('⚠️ Failed to sync MLB game schedule.');
    } finally {
      this.btnNextGame.innerHTML = originalText;
      this.btnNextGame.disabled = false;
    }
  }

  /**
   * Automatically pre-fetches the schedule list for the team in the background and populates the dropdown
   */
  async _populateScheduleDropdown(meta) {
    if (!this.gameSelect) return;

    this.gameSelect.innerHTML = '<option value="">⚾ Loading games...</option>';
    this.gameSelect.disabled = true;

    try {
      const now = new Date();
      const formatDateStr = (d) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const r = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${r}`;
      };

      const startDate = formatDateStr(now);
      const future = new Date();
      future.setDate(now.getDate() + 30);
      const endDate = formatDateStr(future);

      const url = `https://statsapi.mlb.com/api/v1/schedule?sportId=1&teamId=${meta.teamId}&startDate=${startDate}&endDate=${endDate}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error('Network response not ok');
      const data = await response.json();

      let homeGames = [];
      if (data.dates) {
        for (const dateInfo of data.dates) {
          for (const game of dateInfo.games) {
            // Filter only home games and check if they aren't cancelled
            const isHome = game.teams.home.team.id === meta.teamId;
            const isCancelled = ['D', 'C'].includes(game.status.codedGameState); // Delayed or Cancelled
            if (isHome && !isCancelled) {
              homeGames.push(game);
            }
          }
        }
      }

      if (homeGames.length === 0) {
        this.gameSelect.innerHTML = '<option value="">⚾ No games in next 30 days</option>';
        this.gameSelect.disabled = true;
        return;
      }

      // Populate options
      let html = '<option value="">⚾ Select an upcoming game...</option>';
      for (const game of homeGames) {
        const utcDate = new Date(game.gameDate);
        
        // Format dropdown list text in local timezone of the venue
        const optFormatter = new Intl.DateTimeFormat('en-US', {
          timeZone: meta.tz,
          weekday: 'short',
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
        
        const formattedTime = optFormatter.format(utcDate);
        const opponent = game.teams.away.team.name;
        
        // Coded status indicators (e.g. Completed indicator if the game is finished)
        const isFinished = game.status.codedGameState === 'F';
        const suffix = isFinished ? ' (Final)' : '';

        html += `<option value="${game.gameDate}" data-opponent="${opponent}">${formattedTime} - vs. ${opponent}${suffix}</option>`;
      }

      this.gameSelect.innerHTML = html;
      this.gameSelect.disabled = false;

    } catch (err) {
      console.error('Error populating schedule dropdown:', err);
      this.gameSelect.innerHTML = '<option value="">⚾ Error loading schedule</option>';
      this.gameSelect.disabled = true;
    }
  }

  /**
   * Action handler when a user selects a game option from the dropdown list
   */
  _onGameSelectChange(event) {
    const selectedDateStr = event.target.value;
    if (!selectedDateStr) return;

    const state = AppState.getState();
    const meta = getStadiumMetadata(state.stadiumName);
    if (!meta) return;

    const selectedOption = event.target.options[event.target.selectedIndex];
    const opponent = selectedOption.getAttribute('data-opponent') || 'Visitor';

    try {
      const utcDate = new Date(selectedDateStr);
      const partsFormatter = new Intl.DateTimeFormat('en-US', {
        timeZone: meta.tz,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });

      const parts = partsFormatter.formatToParts(utcDate);
      const partVal = (type) => parts.find(p => p.type === type).value;

      const localYear = partVal('year');
      const localMonth = partVal('month');
      const localDay = partVal('day');
      const localHour = parseInt(partVal('hour'));
      const localMinute = parseInt(partVal('minute'));

      const gameLocalDateStr = `${localYear}-${localMonth}-${localDay}`;
      const gameLocalTimeDec = parseFloat((localHour + localMinute / 60).toFixed(2));

      // Update state
      AppState.updateState({
        date: gameLocalDateStr,
        time: gameLocalTimeDec
      });

      // Format printable start time
      const displayHour = localHour > 12 ? localHour - 12 : (localHour === 0 ? 12 : localHour);
      const ampm = localHour >= 12 ? 'PM' : 'AM';
      const timeStr = `${displayHour}:${localMinute.toString().padStart(2, '0')} ${ampm}`;

      this._showNotification(`⚾ Synced to game vs. ${opponent} on ${gameLocalDateStr} at ${timeStr}`);

    } catch (err) {
      console.error('Error syncing select game:', err);
      this._showNotification('⚠️ Failed to sync selected game timezone.');
    } finally {
      // Reset dropdown select to placeholder after sync
      this.gameSelect.value = '';
    }
  }
}
